/**
 * The Family screen's writes (#577 rows 7-9) and its invitation calls (row 8).
 *
 * Every call goes through `api` (`client.js`), the one seam that carries the CSRF token and runs
 * the parent re-check (#578 AD-05): a change made after the five-minute parent window answers 403
 * `PARENT_REAUTH_REQUIRED`, the client prompts once and retries once. Nothing here adds a second
 * proof path.
 *
 * | call | server |
 * | --- | --- |
 * | members | `server/routes/admin.routes.js` (`requireAdmin` on the whole router) |
 * | goals | `server/routes/goal.routes.js`; `userId` names the child, `parentGuard.js` gates it |
 * | household goal | `PUT /api/village` (`village.routes.js`), which starts the village over |
 * | invitations | `server/auth/routes/signup.routes.js` |
 *
 * No role change exists on purpose: the server refuses it by design (`updateUserSchema` has no
 * `role`), so there is no call for it here either.
 */
import { api } from './client.js';
import { newMutationId } from './questPacks.js';

export const INVITATIONS_PATH = '/api/auth/signup/invitations';
export const REMOVED_MEMBERS_PATH = '/api/admin/users/removed';
export const VILLAGE_PATH = '/api/village';

const id = (value) => encodeURIComponent(value);

/** `PUT /api/admin/users/:id` — only the display name; the schema has no role. */
export function renameMember(memberId, displayName) {
  return api.put(`/api/admin/users/${id(memberId)}`, { displayName });
}

/** `DELETE /api/admin/users/:id` — deactivates. The server refuses a parent's own account. */
export function deactivateMember(memberId) {
  return api.delete(`/api/admin/users/${id(memberId)}`);
}

/** `POST /api/admin/users/:id/activate` */
export function reactivateMember(memberId) {
  return api.post(`/api/admin/users/${id(memberId)}/activate`);
}

/** `POST /api/admin/users/:id/restore` — a member the family removed (D-33). */
export function restoreMember(memberId) {
  return api.post(`/api/admin/users/${id(memberId)}/restore`);
}

/** `PUT /api/admin/users/order` — must name EXACTLY the active members. */
export function reorderMembers(orderedUserIds) {
  return api.put('/api/admin/users/order', { orderedUserIds });
}

/**
 * `POST /api/goals` for a child. No `start_date`: the server starts the goal today in the HOUSEHOLD's zone
 * (#583 D-E). This device may be in another zone, or past midnight before the household is.
 */
export function createGoal({ userId, goalType, target }) {
  return api.post('/api/goals', { goal_type: goalType, target_value: target, userId });
}

/** `PUT /api/goals/:id` — `userId` names whose goal it is (#577 row 9). */
export function updateGoal(goalId, { userId, target }) {
  return api.put(`/api/goals/${id(goalId)}`, { target_value: target, userId });
}

/** `DELETE /api/goals/:id?userId=` — the target rides in the query; a DELETE body is unreliable. */
export function deleteGoal(goalId, userId) {
  return api.delete(`/api/goals/${id(goalId)}?userId=${id(userId)}`);
}

/**
 * `PUT /api/village` — replaces the household goal, and the village starts over from now.
 *
 * #583 N1 S4: each save names the project revision it replaces (`revision` from `GET /api/village`, 0 when the
 * family has no goal) and an operation key. A retry of the SAME save (same goal, same revision) reuses its key, so
 * a save whose answer was lost is replayed by the server, never made twice. Any other save gets a new key. While
 * story credits are off, the server ignores both fields, as it always has. One saver per screen.
 */
export function householdGoalSaver(newKey = newMutationId) {
  let pending = null;
  return async ({ name, pointTarget, revision = 0 }) => {
    const signature = JSON.stringify([name, pointTarget, revision]);
    if (pending?.signature !== signature) pending = { signature, operationKey: `village:${newKey()}` };
    const answer = await api.put(VILLAGE_PATH, { name, pointTarget, expectedRevision: revision, operationKey: pending.operationKey });
    pending = null;
    return answer;
  };
}

/** `POST …/invitations/send`. `emailSent` in the answer says whether the email actually went. */
export function sendInvitation(body) {
  return api.post(`${INVITATIONS_PATH}/send`, body);
}

/** `POST …/invitations/:id/resend` — a NEW link; the old one stops working. */
export function resendInvitation(invitationId) {
  return api.post(`${INVITATIONS_PATH}/${id(invitationId)}/resend`);
}

/** `POST …/invitations/:id/revoke` */
export function revokeInvitation(invitationId) {
  return api.post(`${INVITATIONS_PATH}/${id(invitationId)}/revoke`);
}

/**
 * `POST …/invitations/:id/link` — the copy link for ONE pending invitation (decision
 * d-20260924-01). A POST so the server can demand a fresh parent proof: the link is a bearer
 * credential and may carry the parent role. Never stored or logged here.
 */
export function fetchInvitationLink(invitationId) {
  return api.post(`${INVITATIONS_PATH}/${id(invitationId)}/link`);
}
